/**
 * ModerationQueuePage (US-12-GR-06, extended by story 17374)
 *
 * Admin-only paginated list of reported comments, merged from TWO sources:
 *   - Journals comments (adminModerationService.ts)
 *   - Card-thread comments (cardCommentModerationService.ts, story 17374)
 *
 * The two contracts mirror each other exactly (same ReportStatus/ReportOutcome
 * vocabulary, same `{ items, totalCount }` envelope), so this page fetches
 * both with Promise.allSettled and renders one merged list. Each row carries
 * its source (`DisplayRow.source`) so an action always dispatches to the
 * right service with the right id — journal rows key on a comment id
 * (`row.id`), card rows carry BOTH a report id and a card-activity id, kept
 * as separate fields (`reportId` for resolving, `entityId` for hide/unhide)
 * so the two id spaces can never be confused.
 *
 * AC#3 — Admin can Hide / Unhide a comment (either source); reflected
 *         immediately in row state, no reload.
 * AC#4 — Admin can Resolve a report with outcome: NoAction | Hidden | Deleted.
 *         Resolving a card report with Hidden hides the comment server-side
 *         in that same call — the UI must NOT also fire a separate hide.
 * AC#6 — Page is admin-gated (enforced by AdminGate in App.tsx; this page
 *         trusts it has already passed).
 *
 * Errors from the two sources are tracked independently
 * (`journalError` / `cardError`) via Promise.allSettled rather than
 * Promise.all: if one source fails, the other's rows still render and a
 * visible error is shown for the failed one. An empty queue and a broken
 * queue must never look the same — the empty-state message only appears when
 * BOTH sources loaded successfully and both came back with zero rows.
 *
 * States handled: loading, empty, per-source error, paginated merged data.
 */

import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { AlertTriangle, CheckCircle, ChevronLeft, ChevronRight, EyeOff, Eye } from 'lucide-react';
import {
  getReportedComments,
  resolveReport,
  hideComment,
  unhideComment,
} from '../../services/adminModerationService';
import {
  getReportedCardComments,
  resolveCardCommentReport,
  hideCardComment,
  unhideCardComment,
} from '../../services/cardCommentModerationService';
import type {
  ReportedCommentRow,
  CardCommentReportRow,
  ReportOutcome,
  ReportStatus,
} from '../../types/comment';

// ---------------------------------------------------------------------------
// Merged display row — the common shape the table renders from. Built fresh
// from journalRows/cardRows on every render; never stored in reducer state,
// so there is only ever one source of truth per underlying row.
// ---------------------------------------------------------------------------

interface DisplayRow {
  /** Stable React key AND the id used to correlate an in-flight action. */
  key: string;
  source: 'journal' | 'card';
  /** Report id — what gets resolved. Journal: `row.id`. Card: `row.reportId`. */
  reportId: string;
  /** Underlying comment id — what gets hidden/unhidden. Journal: `commentId`. Card: `cardActivityId`. */
  entityId: number;
  text: string | null;
  textTruncated: boolean;
  author: string | null;
  reporterProfileId: string;
  reason: string;
  status: ReportStatus;
  outcome?: ReportOutcome | null;
  timestamp: string;
  isHidden: boolean;
}

function toJournalDisplayRow(row: ReportedCommentRow): DisplayRow {
  return {
    key: `journal:${row.id}`,
    source: 'journal',
    reportId: row.id,
    entityId: row.commentId,
    text: row.commentBody ?? null,
    textTruncated: row.commentBodyTruncated ?? false,
    author: row.commentAuthor ?? null,
    reporterProfileId: row.reporterProfileId,
    reason: row.reason,
    status: row.status,
    outcome: row.outcome,
    timestamp: row.reportedAt,
    // Journals has no explicit "is the comment hidden" field — a standalone
    // hide is reflected by patching outcome to 'Hidden' (see handleHide).
    isHidden: row.outcome === 'Hidden',
  };
}

function toCardDisplayRow(row: CardCommentReportRow): DisplayRow {
  return {
    key: `card:${row.reportId}`,
    source: 'card',
    reportId: row.reportId,
    entityId: row.cardActivityId,
    text: row.commentContent,
    textTruncated: false,
    author: null,
    reporterProfileId: row.reportedByProfileId,
    reason: row.reason,
    status: row.status,
    outcome: row.outcome,
    timestamp: row.createdAtUtc,
    isHidden: row.commentIsHidden,
  };
}

// ---------------------------------------------------------------------------
// State model
// ---------------------------------------------------------------------------

interface QueueState {
  journalRows: ReportedCommentRow[];
  journalTotal: number;
  journalError: string | null;
  cardRows: CardCommentReportRow[];
  cardTotal: number;
  cardError: string | null;
  pageIndex: number;
  pageSize: number;
  statusFilter: ReportStatus | undefined;
  journalLoading: boolean;
  cardLoading: boolean;
  /** Per-row action in-flight: DisplayRow.key → true */
  actionPending: Record<string, boolean>;
}

type SourceResult<T> =
  | { ok: true; rows: T; totalCount: number }
  | { ok: false; error: string };

type QueueAction =
  | { type: 'FETCH_START' }
  | { type: 'JOURNAL_SETTLED'; result: SourceResult<ReportedCommentRow[]> }
  | { type: 'CARD_SETTLED'; result: SourceResult<CardCommentReportRow[]> }
  | { type: 'SET_PAGE'; pageIndex: number }
  | { type: 'SET_FILTER'; status: ReportStatus | undefined }
  | { type: 'ACTION_START'; key: string }
  | { type: 'ACTION_ERROR'; key: string }
  | { type: 'JOURNAL_ACTION_DONE'; key: string; rowId: string; patch: Partial<ReportedCommentRow> }
  | { type: 'CARD_ACTION_DONE'; key: string; reportId: string; patch: Partial<CardCommentReportRow> };

function reducer(state: QueueState, action: QueueAction): QueueState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, journalLoading: true, cardLoading: true };
    // One case per source. Each clears only ITS OWN loading flag and writes only its own
    // rows, so whichever answers first is on screen immediately and a hung source blanks
    // nothing. This is the difference Promise.allSettled does NOT buy: it tolerates a
    // REJECTION, but a request that simply never settles still blocks a combined dispatch.
    case 'JOURNAL_SETTLED': {
      const next: QueueState = { ...state, journalLoading: false };
      if (action.result.ok) {
        next.journalRows = action.result.rows;
        next.journalTotal = action.result.totalCount;
        next.journalError = null;
      } else {
        next.journalError = action.result.error;
      }
      return next;
    }
    case 'CARD_SETTLED': {
      const next: QueueState = { ...state, cardLoading: false };
      if (action.result.ok) {
        next.cardRows = action.result.rows;
        next.cardTotal = action.result.totalCount;
        next.cardError = null;
      } else {
        next.cardError = action.result.error;
      }
      return next;
    }
    case 'SET_PAGE':
      return { ...state, pageIndex: action.pageIndex };
    case 'SET_FILTER':
      return { ...state, statusFilter: action.status, pageIndex: 0 };
    case 'ACTION_START':
      return { ...state, actionPending: { ...state.actionPending, [action.key]: true } };
    case 'ACTION_ERROR': {
      const pending = { ...state.actionPending };
      delete pending[action.key];
      return { ...state, actionPending: pending };
    }
    case 'JOURNAL_ACTION_DONE': {
      const pending = { ...state.actionPending };
      delete pending[action.key];
      const journalRows = state.journalRows.map((r) =>
        r.id === action.rowId ? { ...r, ...action.patch } : r
      );
      return { ...state, actionPending: pending, journalRows };
    }
    case 'CARD_ACTION_DONE': {
      const pending = { ...state.actionPending };
      delete pending[action.key];
      const cardRows = state.cardRows.map((r) =>
        r.reportId === action.reportId ? { ...r, ...action.patch } : r
      );
      return { ...state, actionPending: pending, cardRows };
    }
  }
}

const initialState: QueueState = {
  journalRows: [],
  journalTotal: 0,
  journalError: null,
  cardRows: [],
  cardTotal: 0,
  cardError: null,
  pageIndex: 0,
  pageSize: 20,
  statusFilter: 'Pending',
  journalLoading: false,
  cardLoading: false,
  actionPending: {},
};

// ---------------------------------------------------------------------------
// Status / source badge helpers
// ---------------------------------------------------------------------------

function StatusBadge({ status, outcome }: { status: ReportStatus; outcome?: ReportOutcome | null }) {
  if (status === 'Resolved') {
    const label = outcome ?? 'Resolved';
    const colour =
      outcome === 'Deleted'
        ? 'bg-red-950 text-red-400 border-red-800'
        : outcome === 'Hidden'
          ? 'bg-amber-950 text-amber-400 border-amber-800'
          : 'bg-zinc-800 text-zinc-400 border-zinc-700';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${colour}`}>
        <CheckCircle size={10} aria-hidden="true" />
        {label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-violet-800 bg-violet-950 text-violet-400 text-xs font-medium">
      <AlertTriangle size={10} aria-hidden="true" />
      Pending
    </span>
  );
}

/** Makes the source of a merged row obvious to the reader (AC: source must be obvious). */
function SourceBadge({ source }: { source: DisplayRow['source'] }) {
  return source === 'journal' ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-sky-800 bg-sky-950 text-sky-400 text-xs font-medium">
      Journal
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-fuchsia-800 bg-fuchsia-950 text-fuchsia-400 text-xs font-medium">
      Card
    </span>
  );
}

// ---------------------------------------------------------------------------
// Comment cell — reported comment body, author, and id metadata
// ---------------------------------------------------------------------------

function CommentCell({ row }: { row: DisplayRow }) {
  const [expanded, setExpanded] = useState(false);
  const body = row.text;

  if (body === null) {
    return (
      <>
        <p className="text-zinc-600 text-sm italic">[comment deleted]</p>
        <p className="text-zinc-600 text-xs mt-1">ID {row.entityId}</p>
      </>
    );
  }

  return (
    <>
      {row.author && (
        <p className="text-zinc-400 text-xs font-medium mb-0.5 truncate max-w-[220px]">
          {row.author}
        </p>
      )}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        title={body}
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse comment text' : 'Expand comment text'}
        className={[
          'text-zinc-200 text-sm break-words text-left whitespace-pre-wrap',
          expanded ? '' : 'line-clamp-3',
        ].join(' ')}
      >
        {body}
        {row.textTruncated && <span className="text-zinc-500">…</span>}
      </button>
      <p className="text-zinc-600 text-xs mt-1">ID {row.entityId}</p>
    </>
  );
}

// ---------------------------------------------------------------------------
// Row component
// ---------------------------------------------------------------------------

interface RowProps {
  row: DisplayRow;
  isPending: boolean;
  onResolve: (row: DisplayRow, outcome: ReportOutcome) => void;
  onHide: (row: DisplayRow) => void;
  onUnhide: (row: DisplayRow) => void;
}

function CommentRow({ row, isPending, onResolve, onHide, onUnhide }: RowProps) {
  const isResolved = row.status === 'Resolved';
  const isHidden = row.isHidden;

  return (
    <tr className="border-t border-zinc-800 hover:bg-zinc-900/50 transition-colors">
      {/* Source — must be obvious which queue this row came from */}
      <td className="px-4 py-3">
        <SourceBadge source={row.source} />
      </td>

      {/* Comment content */}
      <td className="px-4 py-3 max-w-xs">
        <CommentCell row={row} />
      </td>

      {/* Reporter / reason */}
      <td className="px-4 py-3 text-sm text-zinc-400">
        <p className="text-xs text-zinc-500 mb-0.5">Reporter</p>
        <p className="font-mono text-xs text-zinc-400 truncate max-w-[120px]">{row.reporterProfileId}</p>
        <p className="text-xs text-zinc-500 mt-1">{row.reason}</p>
      </td>

      {/* Reported at */}
      <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">
        {new Date(row.timestamp).toLocaleString()}
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <StatusBadge status={row.status} outcome={row.outcome} />
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-2 min-w-[220px]">
          {/* Resolve actions — only shown when Pending */}
          {!isResolved && (
            <>
              <button
                type="button"
                disabled={isPending}
                onClick={() => onResolve(row, 'NoAction')}
                className="min-h-[48px] px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs disabled:opacity-50 transition-colors"
                aria-label={`Resolve report ${row.reportId} with no action`}
              >
                No Action
              </button>
              <button
                type="button"
                disabled={isPending}
                onClick={() => onResolve(row, 'Hidden')}
                className="min-h-[48px] px-2.5 py-1 rounded bg-amber-950 hover:bg-amber-900 text-amber-300 text-xs disabled:opacity-50 transition-colors"
                aria-label={`Resolve report ${row.reportId}: hide comment`}
              >
                Hide
              </button>
              <button
                type="button"
                disabled={isPending}
                onClick={() => onResolve(row, 'Deleted')}
                className="min-h-[48px] px-2.5 py-1 rounded bg-red-950 hover:bg-red-900 text-red-300 text-xs disabled:opacity-50 transition-colors"
                aria-label={`Resolve report ${row.reportId}: delete comment (permanent)`}
              >
                Delete
              </button>
            </>
          )}

          {/* Standalone Hide / Unhide (AC#3 — reversible) */}
          {isHidden ? (
            <button
              type="button"
              disabled={isPending}
              onClick={() => onUnhide(row)}
              className="min-h-[48px] px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs disabled:opacity-50 transition-colors flex items-center gap-1"
              aria-label={`Unhide comment ${row.entityId}`}
            >
              <Eye size={12} aria-hidden="true" />
              Unhide
            </button>
          ) : (
            <button
              type="button"
              disabled={isPending || row.outcome === 'Deleted'}
              onClick={() => onHide(row)}
              className="min-h-[48px] px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs disabled:opacity-50 transition-colors flex items-center gap-1"
              aria-label={`Hide comment ${row.entityId}`}
            >
              <EyeOff size={12} aria-hidden="true" />
              Hide
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ModerationQueuePage() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const loadPage = useCallback(async () => {
    dispatch({ type: 'FETCH_START' });

    // Started together, dispatched separately: neither await blocks the other's render.
    const query = {
      status: state.statusFilter,
      pageIndex: state.pageIndex,
      pageSize: state.pageSize,
    };

    const journalDone = getReportedComments(query).then(
      (v) => dispatch({
        type: 'JOURNAL_SETTLED',
        result: { ok: true, rows: v.items, totalCount: v.totalCount },
      }),
      (e: unknown) => dispatch({
        type: 'JOURNAL_SETTLED',
        result: {
          ok: false,
          error: e instanceof Error ? e.message : 'Failed to load Journals comment reports',
        },
      }),
    );

    const cardDone = getReportedCardComments(query).then(
      (v) => dispatch({
        type: 'CARD_SETTLED',
        result: { ok: true, rows: v.items, totalCount: v.totalCount },
      }),
      (e: unknown) => dispatch({
        type: 'CARD_SETTLED',
        result: {
          ok: false,
          error: e instanceof Error ? e.message : 'Failed to load card comment reports',
        },
      }),
    );

    await Promise.all([journalDone, cardDone]);
  }, [state.statusFilter, state.pageIndex, state.pageSize]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const handleResolve = useCallback(async (row: DisplayRow, outcome: ReportOutcome) => {
    dispatch({ type: 'ACTION_START', key: row.key });
    try {
      if (row.source === 'journal') {
        await resolveReport(row.reportId, outcome);
        dispatch({
          type: 'JOURNAL_ACTION_DONE',
          key: row.key,
          rowId: row.reportId,
          patch: { status: 'Resolved', outcome, resolvedAt: new Date().toISOString() },
        });
      } else {
        await resolveCardCommentReport(row.reportId, row.entityId, outcome);
        // Resolving with Hidden hides the comment server-side in this same
        // call — reflect that locally, do NOT also fire hideCardComment.
        const patch: Partial<CardCommentReportRow> = { status: 'Resolved', outcome };
        if (outcome === 'Hidden') patch.commentIsHidden = true;
        dispatch({ type: 'CARD_ACTION_DONE', key: row.key, reportId: row.reportId, patch });
      }
    } catch {
      dispatch({ type: 'ACTION_ERROR', key: row.key });
    }
  }, []);

  const handleHide = useCallback(async (row: DisplayRow) => {
    dispatch({ type: 'ACTION_START', key: row.key });
    try {
      if (row.source === 'journal') {
        await hideComment(row.entityId);
        dispatch({ type: 'JOURNAL_ACTION_DONE', key: row.key, rowId: row.reportId, patch: { outcome: 'Hidden' } });
      } else {
        await hideCardComment(row.entityId);
        dispatch({ type: 'CARD_ACTION_DONE', key: row.key, reportId: row.reportId, patch: { commentIsHidden: true } });
      }
    } catch {
      dispatch({ type: 'ACTION_ERROR', key: row.key });
    }
  }, []);

  const handleUnhide = useCallback(async (row: DisplayRow) => {
    dispatch({ type: 'ACTION_START', key: row.key });
    try {
      if (row.source === 'journal') {
        await unhideComment(row.entityId);
        dispatch({ type: 'JOURNAL_ACTION_DONE', key: row.key, rowId: row.reportId, patch: { outcome: null } });
      } else {
        await unhideCardComment(row.entityId);
        dispatch({
          type: 'CARD_ACTION_DONE',
          key: row.key,
          reportId: row.reportId,
          patch: { commentIsHidden: false },
        });
      }
    } catch {
      dispatch({ type: 'ACTION_ERROR', key: row.key });
    }
  }, []);

  const displayRows = useMemo<DisplayRow[]>(() => {
    const rows = [
      ...state.journalRows.map(toJournalDisplayRow),
      ...state.cardRows.map(toCardDisplayRow),
    ];
    rows.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return rows;
  }, [state.journalRows, state.cardRows]);

  const hasError = state.journalError !== null || state.cardError !== null;
  // Empty only counts when BOTH sources loaded cleanly with nothing to show —
  // an error must never render as though there is nothing to moderate.
  // Work item 17374 follow-up (build 19989). Rows render as soon as THEIR OWN source
  // answers; nothing waits for the slower one. Promise.allSettled handles a rejection but
  // not SLOWNESS, and the mocked E2E proved the difference: the card endpoint was unrouted,
  // never settled, and the Journals rows that had already arrived were never shown.
  const anyLoading = state.journalLoading || state.cardLoading;
  const isEmpty = !anyLoading && !hasError && displayRows.length === 0;
  const totalCount = state.journalTotal + state.cardTotal;
  const totalPages = Math.ceil(totalCount / state.pageSize);

  return (
    <main className="flex-1 p-6 overflow-y-auto space-y-5">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Comment Moderation</h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            Review and act on reported comments (Journals and card threads)
          </p>
        </div>

        {/* Status filter */}
        <div className="flex gap-2" role="group" aria-label="Filter by status">
          {(['Pending', 'Resolved', undefined] as (ReportStatus | undefined)[]).map((s) => {
            const label = s ?? 'All';
            const isActive = state.statusFilter === s;
            return (
              <button
                key={label}
                type="button"
                onClick={() => dispatch({ type: 'SET_FILTER', status: s })}
                className={[
                  'min-h-[48px] px-3 rounded text-sm transition-colors',
                  isActive
                    ? 'bg-violet-700 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200',
                ].join(' ')}
                aria-pressed={isActive}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Loading state */}
      {anyLoading && displayRows.length === 0 && !hasError && (
        <div className="flex items-center justify-center py-16 text-zinc-500" role="status" aria-label="Loading">
          <svg className="animate-spin h-6 w-6 mr-3" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading reported comments…
        </div>
      )}

      {/* Per-source error state — Promise.allSettled means one source can fail
          while the other still has rows below; both are shown independently. */}
      {hasError && (
        <div className="space-y-2">
          {state.journalError && (
            <div
              className="flex items-start gap-3 p-4 rounded-lg bg-red-950 border border-red-800 text-red-300"
              role="alert"
            >
              <AlertTriangle size={18} className="shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="font-medium text-sm">Failed to load Journals comment reports</p>
                <p className="text-xs text-red-400 mt-0.5">{state.journalError}</p>
                <button
                  type="button"
                  onClick={() => void loadPage()}
                  className="mt-2 text-xs text-red-300 underline hover:text-red-100"
                >
                  Retry
                </button>
              </div>
            </div>
          )}
          {state.cardError && (
            <div
              className="flex items-start gap-3 p-4 rounded-lg bg-red-950 border border-red-800 text-red-300"
              role="alert"
            >
              <AlertTriangle size={18} className="shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="font-medium text-sm">Failed to load card comment reports</p>
                <p className="text-xs text-red-400 mt-0.5">{state.cardError}</p>
                <button
                  type="button"
                  onClick={() => void loadPage()}
                  className="mt-2 text-xs text-red-300 underline hover:text-red-100"
                >
                  Retry
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div className="flex flex-col items-center justify-center py-16 text-zinc-500 gap-2">
          <CheckCircle size={40} className="text-zinc-700" aria-hidden="true" />
          <p className="text-sm">No reported comments match this filter.</p>
        </div>
      )}

      {/* Data table — rendered whenever there is at least one row, even if
          the other source errored (never gate a working source on the other). */}
      {displayRows.length > 0 && (
        <div className="rounded-lg border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm" aria-label="Reported comments">
            <thead>
              <tr className="bg-zinc-900 text-zinc-400 text-left text-xs uppercase tracking-wide">
                <th scope="col" className="px-4 py-3 font-medium">Source</th>
                <th scope="col" className="px-4 py-3 font-medium">Comment</th>
                <th scope="col" className="px-4 py-3 font-medium">Reporter / Reason</th>
                <th scope="col" className="px-4 py-3 font-medium">Reported At</th>
                <th scope="col" className="px-4 py-3 font-medium">Status</th>
                <th scope="col" className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row) => (
                <CommentRow
                  key={row.key}
                  row={row}
                  isPending={state.actionPending[row.key] === true}
                  onResolve={handleResolve}
                  onHide={handleHide}
                  onUnhide={handleUnhide}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-zinc-500">
          <span>
            {state.pageIndex * state.pageSize + 1}–
            {Math.min((state.pageIndex + 1) * state.pageSize, totalCount)} of{' '}
            {totalCount}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={state.pageIndex === 0}
              onClick={() => dispatch({ type: 'SET_PAGE', pageIndex: state.pageIndex - 1 })}
              className="min-h-[48px] px-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 disabled:opacity-50 transition-colors flex items-center gap-1"
              aria-label="Previous page"
            >
              <ChevronLeft size={16} aria-hidden="true" />
              Prev
            </button>
            <button
              type="button"
              disabled={state.pageIndex >= totalPages - 1}
              onClick={() => dispatch({ type: 'SET_PAGE', pageIndex: state.pageIndex + 1 })}
              className="min-h-[48px] px-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 disabled:opacity-50 transition-colors flex items-center gap-1"
              aria-label="Next page"
            >
              Next
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
